import PropTypes from 'prop-types'
import queryString from 'query-string'
import { Component } from 'react'
import store from 'store'

import authentication from 'api/authentication'
import companies from 'api/company'
import Email from 'components/authentication/form-fields/Email'
import SetPassword from 'components/authentication/form-fields/SetPassword'
import passwordOutcome from 'components/authentication/passwordOutcome'
import AgreeToTerms from 'components/authentication/registration/register-company/AgreeToTerms'
import CompanyName from 'components/authentication/registration/register-company/CompanyName'
import { SignedOutForm } from 'components/authentication/SignedOutForm'
import SignedOutFormContainer from 'components/authentication/SignedOutFormContainer'
import UpperBackText from 'components/authentication/UpperBackText'
import { withAuthContext } from 'context/with-auth-context'
import toast from 'lib/toast'
import { identify, track } from 'track'

const FORM_STAGES = {
  EMAIL: 'email',
  COMPANY: 'companyName',
  PASSWORD: 'password',
  AGREE_TO_TERMS: 'agreeToTerms'
}

const FORM_STAGE_DETAILS = {
  email: {
    title: 'Register',
    nextStage: FORM_STAGES.COMPANY
  },
  companyName: {
    back: true,
    title: 'Enter your company name',
    nextStage: FORM_STAGES.PASSWORD,
    backStage: FORM_STAGES.EMAIL
  },
  password: {
    back: true,
    title: 'Set your password',
    nextStage: FORM_STAGES.AGREE_TO_TERMS,
    backStage: FORM_STAGES.COMPANY
  },
  agreeToTerms: {
    back: true,
    title: 'Agree to terms',
    backStage: FORM_STAGES.PASSWORD
  }
}

class Register extends Component {
  static propTypes = {
    location: PropTypes.object,
    history: PropTypes.object,
    startTrial: PropTypes.func,
    authState: PropTypes.object
  }

  static defaultProps = {
    startTrial: companies.startTrial
  }

  constructor(props) {
    super(props)
    const { email, code } = queryString.parse(props.location.search)

    this.state = {
      code,
      companyId: '',
      companySecret: '',
      confirmPassword: '',
      email: email || '',
      evaluation: false,
      name: '',
      password: '',
      submitting: false,
      formStage: 'email',
      privacyPolicyAccepted: false
    }
  }

  handleResponse(type, text) {
    this.setState({ submitting: false })
    toast({ type, text })
  }

  handleChange = ({ target }) => {
    this.setState({
      [target.name]: target.value
    })
  }

  handleEnabledChanged = (event) => {
    this.setState({
      evaluation: Boolean(event.target.checked)
    })
  }

  trackSubmission(email) {
    identify(email)
    track('RegistrationSubmission', {
      role: 'super',
      userId: email
    })
  }

  submitDetails = async (event) => {
    event.preventDefault()
    const {
      name,
      email: adminEmail,
      password: adminPassword,
      code
    } = this.state
    const { startTrial, authState } = this.props
    const handleError = (response) => {
      this.handleResponse(
        'error',
        response.data || 'There was an issue, Please try again later'
      )
    }
    this.setState({ submitting: true })

    let trialResponse
    try {
      trialResponse = await startTrial({
        name,
        adminEmail,
        adminPassword,
        code,
        allowUsStorage: true
      })
    } catch (err) {
      handleError(err.response)
    }

    if (!trialResponse) return
    // Sets local storage company for login.
    store.set('company', { name, methods: ['password'] })
    this.trackSubmission(adminEmail)
    this.setState({
      registered: true,
      companyId: trialResponse.identifier,
      companySecret: trialResponse.secret
    })
    this.handleResponse('success', 'Company Registered')

    let credentials
    try {
      credentials = await authentication.signin({
        email: adminEmail,
        companyName: name,
        password: adminPassword
      })
    } catch (err) {
      handleError(err.response)
    }

    if (!credentials) return this.props.history.push('/')

    await authState.startSession(credentials)

    this.props.history.push('/')
  }

  handleBack = () => {
    const backStage = FORM_STAGE_DETAILS[this.state.formStage].backStage
    this.setState({ formStage: backStage })
  }

  submitForm = (event) => {
    event.preventDefault()
    const nextStage = FORM_STAGE_DETAILS[this.state.formStage].nextStage
    const { EMAIL, COMPANY, PASSWORD, AGREE_TO_TERMS } = FORM_STAGES

    switch (this.state.formStage) {
      case EMAIL:
      case COMPANY:
      case PASSWORD:
        this.setState({ formStage: nextStage })
        break
      case AGREE_TO_TERMS:
        this.submitDetails(event)
        break
    }
  }

  handleAcceptPrivacyPolicyChange = () => {
    this.setState({
      privacyPolicyAccepted: !this.state.privacyPolicyAccepted
    })
  }

  formContent = () => {
    const { EMAIL, COMPANY, PASSWORD, AGREE_TO_TERMS } = FORM_STAGES

    switch (this.state.formStage) {
      case EMAIL:
        return <Email email={this.state.email} inputIsDisabled={true} />
      case COMPANY:
        return (
          <CompanyName
            companyName={this.state.name}
            handleChange={this.handleChange}
          />
        )
      case PASSWORD:
        return (
          <SetPassword
            confirmPassword={this.state.confirmPassword}
            handleAcceptPrivacyPolicyChange={
              this.handleAcceptPrivacyPolicyChange
            }
            handleChange={this.handleChange}
            isLoading={this.state.submitting}
            password={this.state.password}
            passwordOutcome={passwordOutcome}
            privacyPolicyAccepted={this.state.privacyPolicyAccepted}
          />
        )
      case AGREE_TO_TERMS:
        return (
          <AgreeToTerms
            evaluation={this.state.evaluation}
            handleEnabledChanged={this.handleEnabledChanged}
            isLoading={this.state.submitting}
          />
        )
    }
  }

  render() {
    const { title, back } = FORM_STAGE_DETAILS[this.state.formStage]
    return (
      <SignedOutFormContainer data-test-submit-registration>
        <UpperBackText onClick={back && this.handleBack}>
          Back to login
        </UpperBackText>
        <SignedOutForm title={title} onSubmit={this.submitForm}>
          {this.formContent()}
        </SignedOutForm>
      </SignedOutFormContainer>
    )
  }
}

export default withAuthContext(Register)
